import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

import Panel from '../ui/Panel';
import CodeBlock from './CodeBlock';

/** Small labelled metric used for the three test-result summary tiles. */
const ResultTile = ({ label, value, emphasis = false }) => (
  <Grid size={{ xs: 12, md: 4 }}>
    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant={emphasis ? 'h4' : 'h6'}
        sx={{ mt: 0.5, fontWeight: 700, color: emphasis ? 'primary.main' : 'text.primary' }}
      >
        {value}
      </Typography>
    </Box>
  </Grid>
);

/**
 * Ad-hoc estimation playground: submit a synthetic ticket and inspect the
 * model's hours, category, reasoning and raw response.
 */
const EstimationTesterSection = ({
  testTicket,
  setTestTicket,
  testSystemPrompt,
  setTestSystemPrompt,
  onTest,
  onClear,
  loading,
  result,
}) => (
  <Panel
    title="Effort Estimation Tester"
    subtitle="Test effort estimation with custom ticket data to see the reasoning and refine the system prompt."
  >
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          size="small"
          label="Ticket Key"
          value={testTicket.key}
          onChange={(e) => setTestTicket({ ...testTicket, key: e.target.value })}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          size="small"
          label="Status"
          value={testTicket.status}
          onChange={(e) => setTestTicket({ ...testTicket, status: e.target.value })}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          size="small"
          required
          label="Summary"
          value={testTicket.summary}
          onChange={(e) => setTestTicket({ ...testTicket, summary: e.target.value })}
          placeholder="e.g. Fix login bug in authentication service"
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          size="small"
          label="Description"
          multiline
          rows={3}
          value={testTicket.description}
          onChange={(e) => setTestTicket({ ...testTicket, description: e.target.value })}
          placeholder="e.g. Users are unable to log in when using OAuth. The error occurs after redirect…"
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          size="small"
          label="System Prompt (optional)"
          multiline
          rows={2}
          value={testSystemPrompt}
          onChange={(e) => setTestSystemPrompt(e.target.value)}
          helperText="Customize the system prompt to guide the estimation model"
        />
      </Grid>
    </Grid>

    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
      <Button
        variant="contained"
        onClick={onTest}
        disabled={loading || !testTicket.summary}
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AutoFixHighIcon />}
      >
        {loading ? 'Testing…' : 'Test Estimation'}
      </Button>
      <Button variant="outlined" onClick={onClear} disabled={loading}>
        Clear
      </Button>
    </Stack>

    {result && (
      <Box sx={{ mt: 3 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Test Results
        </Typography>

        {result.error ? (
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            {result.error}
          </Alert>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <ResultTile label="Estimated Hours" value={result.hours} emphasis />
              <ResultTile label="Category" value={result.category} />
              <ResultTile
                label="System Prompt Used"
                value={result.systemPrompt ? 'Yes' : 'No (default)'}
              />
            </Grid>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Model Reasoning</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <CodeBlock>{result.reasoning || 'No reasoning provided'}</CodeBlock>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Full Prompt Sent to Model</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <CodeBlock>{result.prompt || 'No prompt available'}</CodeBlock>
              </AccordionDetails>
            </Accordion>

            {result.rawResponse && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Raw API Response (debug)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <CodeBlock fontSize="0.75rem">
                    {JSON.stringify(result.rawResponse, null, 2)}
                  </CodeBlock>
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}
      </Box>
    )}
  </Panel>
);

export default EstimationTesterSection;
